import { t } from "i18next";
import { flatten } from "lodash";
import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { put, select, spawn } from "saga-query";

import { encryptPayoad } from "@app/service/helpers";
import { api, thunks } from "@app/state/apis/api";
import {
  selectAllGroupsList,
  selectedGroupData,
} from "@app/state/units/groups";
import { appNotify } from "@app/state/units/notify";

import type { ApiCtx } from "saga-query";
import type { RootState } from "@app/state/rootState";
import type { TProfTreeListItem, IWhoAmI, IGroupMember } from "@app/types";
const identity = {
  id: "0",
  parentId: null,
  ptlTitle: "Contul dvs.",
  ptlClassName: "ptl-identity",
  ptlGroup: "ROOT",
  ptlCfgPage: "whoami",
  ptlSortPosition: 0,
} as TProfTreeListItem;

const REPO_PROF_TREELIST = "ui/profile/treelist";
type TRepoProfTreeList = {
  currentItem: TProfTreeListItem;
};
export const repoProfTreeList = createAssign<TRepoProfTreeList>({
  name: REPO_PROF_TREELIST,
  initialState: {
    currentItem: identity,
  },
});

const selectProfTreeList = (state: RootState) => state[REPO_PROF_TREELIST];
export const selectCurrentProfTreeList = createSelector(
  selectProfTreeList,
  (state) => state.currentItem
);

export const setProfTreeList = thunks.create<TProfTreeListItem>(
  "setProfTreeList",
  function* (ctx, next) {
    const crtstate = yield* select(selectProfTreeList);
    yield* put(
      repoProfTreeList.actions.set({ ...crtstate, currentItem: ctx.payload })
    );
  }
);

export const downloadDistro = api.post(
  "downloadDistro",
  function* (ctx: ApiCtx, next) {
    const { id }: { id: number } = yield* select(
      selectCurrentProfTreeList
    ) as any;
    const groupData = yield* select(selectedGroupData, id);
    if (!id) return;
    ctx.bodyType = "blob";
    const body = {
      id,
      SEED: groupData.SEED,
      PERSEED: groupData.PERSEED,
      GROUP_NAME: groupData.GROUP_NAME,
    };
    ctx.request = ctx.req({
      url: `/distro/${groupData.PERSEED}`,
      method: "POST",
      credentials: "include",
      body: JSON.stringify(encryptPayoad(body)),
    });
    yield* next();
    const { data, ok } = yield ctx.json;
    if (!ok) {
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: "Eroare de acces la descarcarea distributiei",
          duration: 5000,
        })
      );
      return;
    }
    try {
      const blob = new Blob([data], { type: "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "setup.exe";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.log(e);
      yield* spawn(
        appNotify.run,
        appNotify({
          type: "error",
          message: "Eroare la descarcarea distributiei",
          duration: 5000,
        })
      );
    }
  }
);

const addFilesNode = (parentId: string): TProfTreeListItem => {
  return {
    id: `${parentId}-files`,
    parentId: parentId,
    ptlTitle: "Fisiere",
    ptlClassName: "ptl-files",
    ptlGroup: "FILES",
    ptlCfgPage: "distro/files",
    ptlSortPosition: 20,
  } as TProfTreeListItem;
};
const addUsersNode = (parentId: string): TProfTreeListItem => {
  return {
    id: `${parentId}-users`,
    parentId: parentId,
    ptlTitle: "Utilizatori",
    ptlClassName: "ptl-users",
    ptlGroup: "USERS",
    ptlCfgPage: "distro/users",
    ptlSortPosition: 30,
  } as TProfTreeListItem;
};
const addLicenseNode = (parentId: string): TProfTreeListItem => {
  return {
    id: `${parentId}-license`,
    parentId: parentId,
    ptlTitle: "Licente",
    ptlClassName: "ptl-license",
    ptlGroup: "LICENSE",
    ptlCfgPage: "distro/license",
    ptlSortPosition: 40,
  } as TProfTreeListItem;
};

export const selectProfileTreeList = createSelector(
  selectAllGroupsList,
  (allGroups): TProfTreeListItem[] => {
    const treeGroups = (Object.values(allGroups) as IGroupMember[])
      .filter((group) => group.IS_ACTIVE === 1)
      .map((group: IGroupMember) => {
        return {
          id: `${group.GROUPID}`,
          parentId: null,
          ptlTitle: group.GROUP_NAME,
          ptlClassName: "ptl-group",
          ptlGroup: "GROUP",
          ptlCfgPage: "distribution",
          ptlSortPosition: ["SHOW1"].includes(group.PERSEED)
            ? 100
            : ["DEMO0"].includes(group.PERSEED)
            ? 1
            : 10,
          _ptlPerseed: group.PERSEED,
        } as TProfTreeListItem;
      });
    const usualChilds = treeGroups.map((group) => {
      if (["DEMO0", undefined].includes(group._ptlPerseed)) {
        return [];
      }
      const filesNode = addFilesNode(group.id as string);
      const usersNode = addUsersNode(group.id as string);
      const licenseNode = addLicenseNode(group.id as string);
      return [filesNode, usersNode, licenseNode];
    });
    return [identity, ...treeGroups, ...flatten(usualChilds)];
  }
);
